// @flow

import React, { Component, Fragment } from 'react';
import { debounce, get, isEmpty, noop } from 'lodash';

import ApolloQuery from 'web-ssr/app/common/components/ApolloQuery';
import OutsideClickable from 'web-ssr/app/common/components/OutsideClickable';
import { getRef, setRef } from 'web-ssr/app/common/utils/ReactUtils';

import ServicesSearch from './ServicesSearch';
import TrackServiceSearchResults from './TrackServiceSearchResults';
import publicAppSearch from './publicAppSearch';
import { DEBOUNCE_DELAY_TIME } from './constants';

import type { QueryRenderProps } from '@apollo/react-components';

import type { KindType } from 'web-ssr/app/common/types';
import type {
  publicAppSearchQuery,
  publicAppSearchQueryVariables,
} from 'web-ssr/graphql/types';

type Result = QueryRenderProps<
  publicAppSearchQuery,
  publicAppSearchQueryVariables
>;

type Service = {};

type Props = {
  ariaLabel?: string,
  className?: string,
  onFocusOut: () => void,
  onHideResults: (serviceFound: boolean) => void,
  onShowResults: () => void,
  onSelectService: (service: ?Service) => void,
  placeholder?: string,
  kind?: KindType,
  isVisible?: boolean,
  excludeUpcoming?: boolean,
  showEmptyResults?: boolean,
};

type State = {
  debouncedSearchValue: string,
  isDropdownOpen: boolean,
  searchValue: string,
  selectedService: ?Service,
};

class ServicesSearchContainer extends Component<Props, State> {
  state = {
    debouncedSearchValue: '',
    isDropdownOpen: false,
    searchValue: '',
    selectedService: null,
  };

  static defaultProps = {
    excludeUpcoming: true,
    isVisible: true,
    onFocusOut: noop,
    onHideResults: noop,
    onShowResults: noop,
    showEmptyResults: false,
  };

  componentDidUpdate(prevProps: Props) {
    // When the search is revealed, then focus the input text box
    if (!prevProps.isVisible && this.props.isVisible) {
      const searchInputRef = getRef(this, 'searchInputElement');
      setTimeout(() => {
        if (searchInputRef) {
          searchInputRef.focus();
        }
      }, 0);
    }
  }

  onChangeSearchValue = (
    value: string,
    shouldUpdateDebounceValue: boolean = true
  ) => {
    this.setState({
      searchValue: value,
    });

    if (shouldUpdateDebounceValue) {
      this.onChangeDebouncedSearchValue(value);
    }
  };

  onChangeDebouncedSearchValue = debounce((value: string) => {
    this.setState({
      debouncedSearchValue: value,
    });
  }, DEBOUNCE_DELAY_TIME);

  onSearch = (value: string) => {
    this.setState({
      searchValue: value,
    });

    this.onChangeSearchValue(value);

    // Clear the previously selected service whenever we start a new search
    this.setState({
      selectedService: null,
    });

    this.props.onSelectService(null);
  };

  onSelectService = (service: Service) => {
    this.setState({
      selectedService: service,
    });

    // Update the search input with the selected name, so if a user searches
    // `githu` then selects Github, this will result in the search box
    // value being updated to `Github`
    const serviceName: string = get(service, ['name']);
    this.onChangeSearchValue(serviceName, false);

    this.onHideResults(true);
    this.props.onSelectService(service);
  };

  onHideResults = (serviceFound: boolean = false) => {
    const { isDropdownOpen } = this.state;
    this.setState({
      isDropdownOpen: false,
    });

    if (isDropdownOpen) this.props.onHideResults(serviceFound);
  };

  onShowResults = () => {
    this.setState({
      isDropdownOpen: true,
    });
    this.props.onShowResults();
  };

  // When the search is cleared via the X button,
  // return keyboard focus to the textfield.
  onClear = () => {
    const searchInputRef = getRef(this, 'searchInputElement');
    if (searchInputRef) {
      searchInputRef.focus();
    }
  };

  render() {
    const {
      onSelectService,
      onShowResults,
      onHideResults,
      onSearch,
      onClear,
    } = this;
    const {
      ariaLabel,
      placeholder,
      className,
      kind,
      showEmptyResults,
      excludeUpcoming,
      onFocusOut,
    } = this.props;
    const {
      isDropdownOpen,
      searchValue,
      debouncedSearchValue,
      selectedService,
    } = this.state;

    const skipQuery = isEmpty(debouncedSearchValue) && !showEmptyResults;

    const variables = {
      excludeUpcoming,
      limit: 5,
      nameContains: debouncedSearchValue,
    };

    return (
      <OutsideClickable onClickOutside={onHideResults}>
        <ApolloQuery
          query={publicAppSearch}
          skip={skipQuery}
          variables={variables}
        >
          {(result: Result) => {
            const apps = skipQuery
              ? []
              : get(result, ['data', 'getAppsByNameContains', 'edges'], []);
            const isLoading = !skipQuery && get(result, ['loading'], false);

            return (
              <Fragment>
                <TrackServiceSearchResults
                  apps={apps || []}
                  isLoading={isLoading}
                  query={debouncedSearchValue}
                  skipQuery={skipQuery}
                />

                <ServicesSearch
                  apps={apps || []}
                  ariaLabel={ariaLabel}
                  className={className}
                  inputRef={setRef(this, 'searchInputElement')}
                  isDropdownOpen={isDropdownOpen}
                  isLoading={isLoading}
                  kind={kind}
                  onChange={onSearch}
                  onClear={onClear}
                  onFocus={onShowResults}
                  onFocusOut={() => {
                    onHideResults();
                    onFocusOut();
                  }}
                  onSelectService={onSelectService}
                  placeholder={placeholder}
                  selectedService={selectedService}
                  skipQuery={skipQuery}
                  value={searchValue}
                />
              </Fragment>
            );
          }}
        </ApolloQuery>
      </OutsideClickable>
    );
  }
}

export default ServicesSearchContainer;
